// Reaction wording + payload parsing. Pure (no DOM): also bundled into the
// web-push service worker, so notifications read the same everywhere.

export type ReactionEmoji = 'HEART' | 'LAUGH' | 'WOW' | 'TOUCHED' | 'FIRE';
export const REACTION_EMOJI: Record<ReactionEmoji, string> = { HEART: '❤️', LAUGH: '😆', WOW: '😮', TOUCHED: '🥹', FIRE: '🔥' };
const ORDER = Object.keys(REACTION_EMOJI) as ReactionEmoji[];

export interface ReactionActor { id: string | null; name: string; avatarUrl: string | null }
export interface ReactionSummary {
  /** distinct people who reacted */
  count: number;
  /** most recent first, up to 3 */
  actors: ReactionActor[];
  /** [emoji, n] by count desc (ties: canonical order) */
  emojis: [ReactionEmoji, number][];
}

/** FCM data values are strings: accept JSON strings as well as real values. */
function json(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (!t) return undefined;
  if (t[0] === '[' || t[0] === '{') { try { return JSON.parse(t); } catch { return v; } }
  return v;
}

const isEmoji = (e: unknown): e is ReactionEmoji => typeof e === 'string' && e in REACTION_EMOJI;

export function sortEmojiCounts(m: Partial<Record<string, number>>): [ReactionEmoji, number][] {
  return ORDER.map((e) => [e, Math.max(0, Number(m[e]) || 0)] as [ReactionEmoji, number])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || ORDER.indexOf(a[0]) - ORDER.indexOf(b[0]));
}

/**
 * Merged REACTION notification data, current shape
 *   { count, actors: [{id, name, avatarUrl}], emojis: {HEART: 3, …} }
 * and older ones (actors: ["Lan", …] / "Lan,Huy"; emojis: ["HEART", …]; emoji: "HEART").
 * `fallback` = the notification's own actor (actorId/actorName/actorAvatar).
 */
export function parseReaction(data: Record<string, unknown> | undefined, fallback?: Partial<ReactionActor>): ReactionSummary {
  const d = data ?? {};
  // actors
  let raw = json(d.actors);
  if (typeof raw === 'string') raw = raw.split(',').map((s) => s.trim()).filter(Boolean);
  const actors: ReactionActor[] = (Array.isArray(raw) ? raw : []).map((a): ReactionActor | null => {
    if (typeof a === 'string') return { id: null, name: a, avatarUrl: null };
    if (a && typeof a === 'object') {
      const o = a as Record<string, unknown>;
      const name = String(o.name ?? o.displayName ?? '').trim();
      return name ? { id: o.id ? String(o.id) : null, name, avatarUrl: o.avatarUrl ? String(o.avatarUrl) : null } : null;
    }
    return null;
  }).filter((a): a is ReactionActor => !!a);
  if (!actors.length && fallback?.name) actors.push({ id: fallback.id ?? null, name: fallback.name, avatarUrl: fallback.avatarUrl ?? null });

  // emojis
  const counts: Record<string, number> = {};
  const e = json(d.emojis);
  if (Array.isArray(e)) for (const k of e) { if (isEmoji(k)) counts[k] = (counts[k] ?? 0) + 1; }
  else if (e && typeof e === 'object') for (const [k, n] of Object.entries(e as Record<string, unknown>)) { if (isEmoji(k)) counts[k] = Number(n) || 0; }
  else if (typeof e === 'string') for (const k of e.split(',')) { const t = k.trim(); if (isEmoji(t)) counts[t] = (counts[t] ?? 0) + 1; }
  if (isEmoji(d.emoji) && !counts[d.emoji]) counts[d.emoji] = 1;

  const emojiTotal = Object.values(counts).reduce((a, b) => a + b, 0);
  const count = Math.max(1, Number(d.count) || 0, actors.length, emojiTotal ? 1 : 0);
  return { count, actors: actors.slice(0, 3), emojis: sortEmojiCounts(counts) };
}

export interface TextPart { text: string; bold?: boolean }

/**
 * 1: "**Lan** đã thả cảm xúc bài của bạn"
 * 2: "**Lan** và **Huy** đã thả cảm xúc bài của bạn"
 * 3+: "**Lan**, **Huy** và 3 người khác đã thả cảm xúc bài của bạn"
 */
export function reactionParts(r: Pick<ReactionSummary, 'count' | 'actors'>): TextPart[] {
  const names = r.actors.map((a) => a.name);
  const n = Math.max(r.count, names.length, 1);
  const first = names[0] ?? 'Ai đó';
  const tail: TextPart = { text: ' đã thả cảm xúc bài của bạn' };
  if (n === 1) return [{ text: first, bold: true }, tail];
  if (n === 2) {
    return names[1]
      ? [{ text: first, bold: true }, { text: ' và ' }, { text: names[1], bold: true }, tail]
      : [{ text: first, bold: true }, { text: ' và 1 người khác' }, tail];
  }
  if (!names[1]) return [{ text: first, bold: true }, { text: ` và ${n - 1} người khác` }, tail];
  return [{ text: first, bold: true }, { text: ', ' }, { text: names[1], bold: true }, { text: ` và ${n - 2} người khác` }, tail];
}

export const reactionSentence = (r: Pick<ReactionSummary, 'count' | 'actors'>) => reactionParts(r).map((p) => p.text).join('');

/** "❤️ 3 · 😆 2" */
export const emojiLine = (emojis: [ReactionEmoji, number][]) => emojis.map(([e, n]) => `${REACTION_EMOJI[e]} ${n}`).join(' · ');
